(() => {
  const WIDGET_SELECTOR = ".jdgm-rev-widg";
  const JUDGEME_SCOPE_SELECTOR =
    ".jdgm-legacy-widget-content, .jdgm-widget, .jdgm-review-widget, .jdgm-rev-widg";

  let pending = false;
  let domObserver = null;
  const inlineColorObservers = new WeakMap();

  const toNumber = (value) => {
    const match = String(value || "").match(/[0-9]+(?:\.[0-9]+)?/);
    return match ? Number(match[0]) : null;
  };

  const formatRating = (value) => {
    const rating = toNumber(value);
    if (rating === null || Number.isNaN(rating)) return "";
    return rating.toFixed(2);
  };

  const formatCount = (value) => {
    const raw = String(value || "").trim();
    const match = raw.match(/[0-9,]+/);
    return match ? match[0] : raw;
  };

  const parseDate = (raw) => {
    if (!raw) return "";

    const text = String(raw).trim();
    const dateFromData = text.includes("UTC")
      ? new Date(text.replace(" UTC", "Z"))
      : new Date(text);

    if (!Number.isNaN(dateFromData.getTime())) {
      return dateFromData.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    }

    const numeric = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

    if (numeric) {
      const [, month, day, year] = numeric;

      return new Date(
        Date.UTC(Number(year), Number(month) - 1, Number(day)),
      ).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    }

    return text;
  };

  const setText = (node, value) => {
    if (!node) return;

    const next = String(value || "");

    if (node.textContent !== next) {
      node.textContent = next;
    }
  };

  const setAttribute = (node, name, value) => {
    if (!node) return;

    const next = String(value);

    if (node.getAttribute(name) !== next) {
      node.setAttribute(name, next);
    }
  };

  const getOriginalText = (node, key) => {
    if (!node) return "";

    const attr = `data-cd-jdgm-original-${key}`;
    const saved = node.getAttribute(attr);

    if (saved !== null) return saved;

    const current = node.textContent || "";
    node.setAttribute(attr, current);

    return current;
  };

  const cleanInlineColorNode = (node) => {
    if (!(node instanceof HTMLElement)) return false;

    const before = node.getAttribute("style");

    if (!before || !/color\s*:/i.test(before)) return false;

    const keptDeclarations = before
      .split(";")
      .map((declaration) => declaration.trim())
      .filter((declaration) => {
        if (!declaration) return false;

        const propName = declaration.split(":")[0];

        if (!propName) return false;

        return propName.trim().toLowerCase() !== "color";
      });

    if (keptDeclarations.length) {
      node.setAttribute("style", `${keptDeclarations.join("; ")};`);
    } else {
      node.removeAttribute("style");
    }

    return before !== node.getAttribute("style");
  };

  const removeInlineTextColors = (root) => {
    if (!root) return;

    const scope = root.closest?.(JUDGEME_SCOPE_SELECTOR) || root;

    cleanInlineColorNode(scope);

    scope.querySelectorAll("[style]").forEach((node) => {
      cleanInlineColorNode(node);
    });
  };

  const watchInlineTextColors = (widget) => {
    if (!widget) return;

    const scope = widget.closest(JUDGEME_SCOPE_SELECTOR) || widget;

    if (!scope || inlineColorObservers.has(scope)) return;

    removeInlineTextColors(scope);

    const colorObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          cleanInlineColorNode(mutation.target);
          return;
        }

        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            cleanInlineColorNode(node);

            node.querySelectorAll?.("[style]").forEach((child) => {
              cleanInlineColorNode(child);
            });
          });
        }
      });
    });

    colorObserver.observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    inlineColorObservers.set(scope, colorObserver);
  };

  const enhanceSummary = (widget) => {
    const summaryInner = widget.querySelector(".jdgm-rev-widg__summary-inner");
    const average = widget.querySelector(".jdgm-rev-widg__summary-average");
    const summaryText = widget.querySelector(".jdgm-rev-widg__summary-text");

    if (summaryInner && average) {
      average.classList.add("cd-jdgm-summary-number");

      const originalAverage = getOriginalText(average, "average");
      const rating = formatRating(
        widget.getAttribute("data-average-rating") ||
          originalAverage ||
          average.textContent,
      );

      if (rating) {
        setText(average, rating);
      }

      if (summaryText && average.nextElementSibling !== summaryText) {
        summaryInner.insertBefore(average, summaryText);
      } else if (!summaryText && average.parentElement !== summaryInner) {
        summaryInner.insertBefore(average, summaryInner.firstChild);
      }
    }

    if (summaryText) {
      const originalSummary = getOriginalText(summaryText, "summary");
      const count = formatCount(
        widget.getAttribute("data-number-of-reviews") ||
          originalSummary ||
          summaryText.textContent,
      );

      if (count) {
        setText(summaryText, `Based on ${count} reviews`);
      }
    }
  };

  const enhanceHistogram = (widget) => {
    widget
      .querySelectorAll(".jdgm-histogram__frequency")
      .forEach((frequency) => {
        const originalFrequency = getOriginalText(frequency, "frequency");
        const number = formatCount(originalFrequency || frequency.textContent);

        if (number) {
          setText(frequency, `(${number})`);
        }
      });

    widget.querySelectorAll(".jdgm-histogram__row").forEach((row) => {
      const bar = row.querySelector(".jdgm-histogram__bar-content");
      const percentage = row.getAttribute("data-percentage");

      if (bar && percentage && bar.style.width !== `${percentage}%`) {
        bar.style.width = `${percentage}%`;
      }
    });
  };

  const enhanceReview = (review) => {
    const title = review.querySelector(".jdgm-rev__title");
    const body = review.querySelector(".jdgm-rev__body");
    const content = review.querySelector(".jdgm-rev__content");
    const author = review.querySelector(".jdgm-rev__author");
    const timestamp = review.querySelector(".jdgm-rev__timestamp");

    review.classList.add("cd-jdgm-enhanced");

    if (title && !title.textContent.trim()) {
      review.classList.add("cd-jdgm-empty-title");
    } else {
      review.classList.remove("cd-jdgm-empty-title");
    }

    if (!content) return;

    const authorText = author ? author.textContent.trim() : "";
    const dateText = timestamp
      ? parseDate(
          timestamp.getAttribute("data-content") || timestamp.textContent,
        )
      : "";
    const metaText = [authorText, dateText].filter(Boolean).join(" · ");

    const existingMetas = Array.from(
      content.querySelectorAll(".cd-jdgm-review-meta"),
    );

    let meta = existingMetas[0];

    if (!meta) {
      meta = document.createElement("div");
      meta.className = "cd-jdgm-review-meta";
      content.appendChild(meta);
    }

    existingMetas.slice(1).forEach((duplicateMeta) => {
      duplicateMeta.remove();
    });

    if (body && meta.previousElementSibling !== body) {
      body.insertAdjacentElement("afterend", meta);
    }

    setText(meta, metaText);
  };

  const enhancePagination = (widget) => {
    widget.querySelectorAll(".jdgm-paginate__next-page").forEach((link) => {
      setText(link, "Load more");
      setAttribute(link, "title", "Load more reviews");
      setAttribute(link, "data-cd-jdgm-load-more", "true");
    });
  };

  const enhanceWidget = (widget) => {
    if (!widget) return;

    widget.classList.add("cd-jdgm-figma-ready");

    watchInlineTextColors(widget);
    removeInlineTextColors(widget);

    enhanceSummary(widget);
    enhanceHistogram(widget);
    widget.querySelectorAll(".jdgm-rev").forEach(enhanceReview);
    enhancePagination(widget);

    removeInlineTextColors(widget);
  };

  const startDomObserver = () => {
    if (domObserver || !document.documentElement) return;

    domObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== "childList") continue;

        const changedInsideWidget = Array.from(mutation.addedNodes).some(
          (node) => {
            if (!(node instanceof Element)) return false;

            return (
              node.matches?.(WIDGET_SELECTOR) ||
              node.closest?.(WIDGET_SELECTOR) ||
              node.querySelector?.(WIDGET_SELECTOR)
            );
          },
        );

        if (changedInsideWidget) {
          schedule();
          return;
        }
      }
    });

    domObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const stopDomObserver = () => {
    if (!domObserver) return;

    domObserver.disconnect();
    domObserver = null;
  };

  const run = () => {
    pending = false;

    stopDomObserver();

    try {
      document.querySelectorAll(WIDGET_SELECTOR).forEach(enhanceWidget);
    } finally {
      startDomObserver();
    }
  };

  const schedule = () => {
    if (pending) return;

    pending = true;
    window.requestAnimationFrame(run);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule, { once: true });
  } else {
    schedule();
  }

  window.addEventListener("load", schedule, { once: true });
  document.addEventListener("jdgm.loaded", schedule);
  document.addEventListener("jdgm.reviewsLoaded", schedule);

  startDomObserver();
})();
